/**
 * Public profile — what instructing solicitors and the public can see, and the barrister's
 * hand-curated notable cases.
 *
 * Route: /barrister/settings/public-profile
 *
 * This editor lived inside the old My Profile page. When that page's name, contact and
 * credential fields moved into Settings → My Account, this part was deleted with it and nothing
 * else wrote `PATCH /me/public-profile` — the visibility switches and notable cases could be
 * viewed on /barrister/profile but no longer changed. Restored here as it was.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, Gavel, PencilSimple, Plus, Trash, CircleNotch, ArrowSquareOut } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { fetchPublicProfile, updatePublicProfile, type NotableCase } from '@/lib/meApi';

const emptyCase = (): NotableCase => ({ title: '', citation: '', year: new Date().getFullYear(), summary: '' });

const VISIBILITY_ITEMS = [
  { key: 'matters' as const,   label: 'Show Active Matters', desc: 'Number of matters currently in progress', rec: true },
  { key: 'judgments' as const, label: 'Show Judgments',      desc: 'Total judgment count for your practice',  rec: true },
  { key: 'winRate' as const,   label: 'Show Win Rate',       desc: 'Percentage of closed matters won — off by default as it can be misleading', rec: false },
];

export default function PublicProfileSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pubQ = useQuery({
    queryKey: ['public-profile'],
    queryFn: fetchPublicProfile,
    staleTime: 0,
    retry: 1,
    // This page is a FORM sitting on top of the query. A background refetch landing
    // mid-edit replaced the user's unsaved switches with the stored copy, which is
    // what made a switch appear to need two taps. Nothing here needs focus polling.
    refetchOnWindowFocus: false,
  });
  const pub = pubQ.data;

  const [vis, setVis] = useState({ winRate: false, matters: true, judgments: true });
  const [notableCases, setNotableCases] = useState<NotableCase[]>([]);
  const [saving, setSaving] = useState(false);
  const [addingCase, setAddingCase] = useState(false);
  const [caseForm, setCaseForm] = useState<NotableCase>(emptyCase());
  const [editingCaseIdx, setEditingCaseIdx] = useState<number | null>(null);

  /**
   * Seed the form from the stored profile ONCE.
   *
   * This effect used to run on every `pub` change, and `pub` is a new object on each
   * refetch — so any refetch overwrote unsaved switches AND discarded notable cases the
   * barrister had just added or removed. On a phone the triggers fire constantly (keyboard
   * opening, app switching, rotation), so the page looked like it needed checking twice.
   */
  const seeded = useRef(false);
  useEffect(() => {
    if (!pub || seeded.current) return;
    seeded.current = true;
    setVis({ winRate: pub.barrister.visibility.winRate, matters: pub.barrister.visibility.matters, judgments: pub.barrister.visibility.judgments });
    setNotableCases(pub.barrister.notableCases);
  }, [pub]);

  const handleAddCase = () => {
    if (!caseForm.title || !caseForm.citation) { toast.error('Title and citation are required'); return; }
    if (editingCaseIdx !== null) {
      setNotableCases((prev) => prev.map((c, i) => (i === editingCaseIdx ? { ...caseForm } : c)));
      setEditingCaseIdx(null);
    } else {
      setNotableCases((prev) => [...prev, { ...caseForm }]);
    }
    setCaseForm(emptyCase());
    setAddingCase(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePublicProfile({
        publicShowWinRate: vis.winRate,
        publicShowMatters: vis.matters,
        publicShowJudgments: vis.judgments,
        notableCases,
      });
      // There is nothing unsaved left to protect, so let the stored copy re-seed the
      // form: the barrister then sees exactly what was persisted, not what was typed.
      seeded.current = false;
      await qc.invalidateQueries({ queryKey: ['public-profile'] });
      toast.success('Public profile saved');
    } catch {
      toast.error('Failed to save public profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl pb-24">
      <PageHeader
        title="Public profile"
        subtitle="Control what instructing solicitors and the public can see, and highlight the cases you choose."
        onBack={() => navigate(-1)}
      />

      <div className="mb-4 flex justify-end">
        <Link to="/barrister/profile" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
          View your public profile <ArrowSquareOut size={12} />
        </Link>
      </div>

      {pubQ.isLoading ? (
        <div className="flex h-40 items-center justify-center"><CircleNotch className="h-5 w-5 animate-spin text-muted-foreground/40" /></div>
      ) : pubQ.isError || !pub ? (
        <div className="rounded-xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          Your public profile could not be loaded.
          <button type="button" onClick={() => void pubQ.refetch()} className="ml-2 font-medium text-foreground underline">Try again</button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="mb-5 flex items-start gap-3 border-b border-border/50 pb-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
              <Eye size={14} weight="bold" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">Public Profile Visibility</div>
              <div className="mt-0.5 text-xs text-muted-foreground">Control what instructing solicitors and the public can see</div>
            </div>
          </div>

          <div className="mb-6 space-y-3">
            {VISIBILITY_ITEMS.map((item) => (
              <div key={item.key} className="flex items-start justify-between rounded-xl border border-border/50 px-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                    {item.label}
                    {!item.rec && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-bold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">Off by default</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{item.desc}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={vis[item.key]}
                  aria-label={item.label}
                  onClick={() => setVis((v) => ({ ...v, [item.key]: !v[item.key] }))}
                  className={`relative ml-4 h-5 w-9 shrink-0 rounded-full transition-colors ${vis[item.key] ? 'bg-brand-600' : 'bg-muted'}`}
                >
                  <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all ${vis[item.key] ? 'left-4' : 'left-0.5'}`} />
                </button>
              </div>
            ))}
          </div>

          {/* Notable cases editor */}
          <div className="border-t border-border/50 pt-5">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Gavel size={13} className="text-muted-foreground" />
                  Notable Cases
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">Manually curated — only add publicly reported cases you choose to highlight</div>
              </div>
              {!addingCase && (
                <button
                  type="button"
                  onClick={() => { setAddingCase(true); setEditingCaseIdx(null); setCaseForm(emptyCase()); }}
                  className="flex items-center gap-1.5 rounded-xl border border-brand-200 bg-brand-50 px-3 py-1.5 text-xs font-semibold text-brand-700 transition hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300 dark:hover:bg-brand-500/25"
                >
                  <Plus size={11} weight="bold" />Add case
                </button>
              )}
            </div>

            {addingCase && (
              <div className="mb-3 space-y-3 rounded-xl border border-brand-200/60 bg-brand-50/30 p-4 dark:border-brand-500/30 dark:bg-brand-500/10">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Case Name *</label>
                    <input value={caseForm.title} onChange={(e) => setCaseForm((f) => ({ ...f, title: e.target.value }))}
                      placeholder="R v Smith" className="w-full rounded-lg border border-border/70 bg-background px-3 py-2 text-xs focus:border-brand-400 focus:outline-none" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Citation *</label>
                    <input value={caseForm.citation} onChange={(e) => setCaseForm((f) => ({ ...f, citation: e.target.value }))}
                      placeholder="[2024] EWCA Crim 123" className="w-full rounded-lg border border-border/70 bg-background px-3 py-2 text-xs focus:border-brand-400 focus:outline-none" />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Year</label>
                  <input type="number" value={caseForm.year} onChange={(e) => setCaseForm((f) => ({ ...f, year: parseInt(e.target.value) || new Date().getFullYear() }))}
                    className="w-24 rounded-lg border border-border/70 bg-background px-3 py-2 text-xs focus:border-brand-400 focus:outline-none" />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Brief Summary</label>
                  <textarea value={caseForm.summary} onChange={(e) => setCaseForm((f) => ({ ...f, summary: e.target.value }))}
                    placeholder="Your role and outcome in one sentence..."
                    rows={2} className="w-full resize-none rounded-lg border border-border/70 bg-background px-3 py-2 text-xs focus:border-brand-400 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button type="button" onClick={handleAddCase}
                    className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700">
                    {editingCaseIdx !== null ? 'Save changes' : 'Add case'}
                  </button>
                  <button type="button" onClick={() => { setAddingCase(false); setEditingCaseIdx(null); }}
                    className="rounded-lg border border-border px-3.5 py-1.5 text-xs text-muted-foreground transition hover:bg-muted/50">
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {notableCases.map((nc, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-border/50 px-3.5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground">
                      {nc.title}
                      <span className="ml-2 text-[10px] text-muted-foreground">{nc.citation}</span>
                      <span className="ml-1.5 text-[10px] text-muted-foreground/60">{nc.year}</span>
                    </div>
                    {nc.summary && <div className="mt-0.5 text-xs text-muted-foreground">{nc.summary}</div>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button type="button" aria-label="Edit case" onClick={() => { setEditingCaseIdx(i); setCaseForm({ ...nc }); setAddingCase(true); }}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-muted/50">
                      <PencilSimple size={11} />
                    </button>
                    <button type="button" aria-label="Remove case" onClick={() => setNotableCases((prev) => prev.filter((_, idx) => idx !== i))}
                      className="rounded-lg p-1.5 text-muted-foreground transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400">
                      <Trash size={11} />
                    </button>
                  </div>
                </div>
              ))}
              {notableCases.length === 0 && !addingCase && (
                <div className="rounded-xl border border-dashed border-border/50 py-5 text-center text-xs text-muted-foreground/60">
                  No cases added yet — add publicly reported cases to build your public profile
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 flex justify-end">
            <button type="button" onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 rounded-xl bg-brand-600 px-5 py-2 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-60">
              {saving ? <><CircleNotch size={13} className="animate-spin" />Saving…</> : <><Eye size={13} weight="bold" />Save public profile</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
